import './ProfileContent.scss';
import { useForm } from '@form-ts/react';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useAction } from 'src/hooks/useAction';
import { useBoundAction } from 'src/hooks/useBoundAction';
import { Avatar } from 'src/modules/common/components/Avatar';
import { UserForm } from 'src/modules/common/components/UserForm';
import { UserFormData } from 'src/modules/common/types/UserFormData';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { AssetField } from 'src/modules/form/components/AssetField';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { getLoggedInUser } from 'src/modules/login/selectors/getLoggedInUser';
import { PROFILE_CHANGE, PROFILE_CHANGE_PASSWORD } from 'src/modules/profile/actions/ProfileActions';
import { CHANGE_PASSWORD_SCHEMA } from 'src/modules/profile/constants/schema';
import { ChangePasswordData } from 'src/modules/profile/types/ChangePasswordData';
import { PasswordChangeForm } from 'src/modules/profile/views/PasswordChangeForm';
import { ProfileHeader } from 'src/modules/profile/views/ProfileHeader';
import { USER_PROFILE_EDIT_FORM_SCHEMA } from 'src/modules/users/constants/userProfileEditFormSchema';

export const ProfileContent = (): React.ReactElement => {
  const authUser = useSelector(getLoggedInUser);

  const profileForm = useForm<UserFormData, FormError>('profile.user-settings', {
    initialValues: {
      email: authUser.email,
      firstName: authUser.firstName,
      lastName: authUser.lastName,
      jobTitle: authUser.jobTitle || '',
      avatar: authUser.avatar,
    },
    reinitialize: false,
  });

  const changePasswordForm = useForm<ChangePasswordData, FormError>('profile.password-change', {
    initialValues: {
      confirmation: '',
      password: '',
    },
    reinitialize: false,
  });

  useFormValidator(profileForm, USER_PROFILE_EDIT_FORM_SCHEMA);
  useFormValidator(changePasswordForm, CHANGE_PASSWORD_SCHEMA);

  const changeProfile = useAction(PROFILE_CHANGE.trigger);
  const changePassword = useBoundAction(PROFILE_CHANGE_PASSWORD.request, changePasswordForm);

  useEffect(() => profileForm.subscribe((prevState) => {
    const nextState = profileForm.currentState;
    if (prevState.values !== nextState.values) {
      changeProfile(nextState.values);
    }
  }), [profileForm, changeProfile]);

  return (
    <>
      <ProfileHeader
        title={`${authUser.firstName} ${authUser.lastName}`}
      />

      <div className="bp-profile-content">
        <div className="bp-profile-content__body">
          <div className="bp-profile-content__tile">
            <BodyMedium01>
              <FormattedMessage id="profile/form/section/userSettings/title"/>
            </BodyMedium01>

            <UserForm
              disableEmail={true}
              form={profileForm}
              isUserCreation={false}
            />
          </div>

          <div className="bp-profile-content__tile">
            <BodyMedium01>
              <FormattedMessage id="profile/form/section/changePassword/title"/>
            </BodyMedium01>

            <PasswordChangeForm form={changePasswordForm} onChange={changePassword}/>
          </div>

          <div className="bp-profile-content__tile">
            <BodyMedium01>
              <FormattedMessage id="profile/form/section/changeAvatar/title"/>
            </BodyMedium01>

            <Avatar user={authUser} showDetails={true}/>

            <AssetField
              field={profileForm.field.at('avatar')}
            />
          </div>
        </div>
      </div>
    </>
  );
};
